import { useRef } from "react";
import { motion, useInView } from "framer-motion";

const AREAS = [
  { name: "Atracción", score: 20 },
  { name: "Selección", score: 14 },
  { name: "Onboarding", score: 18 },
  { name: "Desempeño", score: 12 },
  { name: "Comunicación", score: 20 },
  { name: "Retención", score: 16 },
];

const MAX_AREA_SCORE = 20; // maximum possible points per area
const AVG_AREA_SCORE = AREAS.reduce((s, a) => s + a.score, 0) / AREAS.length;

/**
 * Editorial infographic: 6-area score visualization mimicking the report
 * output of the Talent Health Check. Bars animate on scroll into view.
 */
export default function AreaScoreChart() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });

  return (
    <div
      ref={ref}
      data-testid="area-score-chart"
      className="relative border border-white/10 bg-[#12362D] backdrop-blur-sm p-8 lg:p-10 rounded-2xl"
    >
      {/* corner marks */}
      <div className="absolute top-3 left-3 w-3 h-px bg-[#C9A84C]" />
      <div className="absolute top-3 left-3 w-px h-3 bg-[#C9A84C]" />
      <div className="absolute top-3 right-3 w-3 h-px bg-[#C9A84C]" />
      <div className="absolute top-3 right-3 w-px h-3 bg-[#C9A84C]" />
      <div className="absolute bottom-3 left-3 w-3 h-px bg-[#C9A84C]" />
      <div className="absolute bottom-3 left-3 w-px h-3 bg-[#C9A84C]" />
      <div className="absolute bottom-3 right-3 w-3 h-px bg-[#C9A84C]" />
      <div className="absolute bottom-3 right-3 w-px h-3 bg-[#C9A84C]" />

      <div className="flex items-baseline justify-between mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-white/50 mb-2">
            Informe de resultados
          </div>
          <div className="font-display text-xl text-white">6 áreas evaluadas</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.24em] text-white/50 mb-1">Puntuación global</div>
          <div className="font-serif-italic text-4xl brand-gradient-text leading-none">100</div>
        </div>
      </div>

      <ul className="space-y-4">
        {AREAS.map((a, i) => (
          <li key={a.name} className="grid grid-cols-[110px_1fr_40px] items-center gap-4">
            <span className="text-[12px] uppercase tracking-[0.14em] text-white/70">{a.name}</span>
            <div className="h-[6px] bg-white/10 rounded-full overflow-hidden">
              <motion.div
                initial={{ scaleX: 0 }}
                animate={inView ? { scaleX: a.score / MAX_AREA_SCORE } : { scaleX: 0 }}
                transition={{ duration: 1.1, delay: 0.15 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                style={{ originX: 0 }}
                className={`h-full w-full rounded-full ${
                  a.score < AVG_AREA_SCORE ? "bg-[#C9A84C]" : "brand-gradient"
                }`}
              />
            </div>
            <span className="text-[13px] font-display text-white/90 text-right tabular-nums">{a.score}</span>
          </li>
        ))}
      </ul>

      <div className="mt-8 pt-6 border-t border-white/10 flex items-center justify-between text-[10px] uppercase tracking-[0.22em] text-white/40">
        <span>Áreas en amarillo → prioridad</span>
      </div>
    </div>
  );
}
